import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layout/MainLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, Printer, Check, FileText, ShieldAlert, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  ContinuityResolutionDoc, StandingSuccessorResolutionDoc, IncapacityProtocolDoc,
  FoundersLetterDoc, SuccessorsManualDoc, SuccessorManagerAcceptanceDoc,
  ReadinessTransitionResolutionDoc, DOC_TEACHING, DOC_GUIDANCE, FOUNDERS_PROMPTS,
  type RenderContext,
} from "@/components/succession/documents/renderers";

type DocRow = {
  id: string;
  doc_type: string;
  title: string;
  status: "drafted" | "signed_filed";
  content: any;
  filed_at: string | null;
};

interface ApplicableDoc {
  doc_type: string;
  title: string;
}

async function resolveAdminUna(userId: string): Promise<{ id: string; name: string } | null> {
  const { data: unaAsTS } = await supabase
    .from("unas").select("id, name").eq("treasury_secretary_id", userId).maybeSingle();
  if (unaAsTS) return { id: unaAsTS.id as string, name: (unaAsTS.name as string) || "" };
  const { data: member } = await supabase
    .from("una_members")
    .select("una_id, una:una_id(name)")
    .eq("user_id", userId).eq("status", "active")
    .in("member_role", ["managing_member", "treasury_secretary"])
    .limit(1).maybeSingle();
  if (!member?.una_id) return null;
  return { id: member.una_id as string, name: (member.una as any)?.name || "" };
}

function buildApplicable(ctx: RenderContext): ApplicableDoc[] {
  const list: ApplicableDoc[] = [
    { doc_type: "continuity_resolution", title: "Private Stewardship Continuity Resolution" },
    { doc_type: "standing_successor_resolution", title: "Standing Successor Resolution" },
    { doc_type: "incapacity_protocol", title: "Incapacity Protocol" },
    { doc_type: "founders_letter", title: "The Founder's Letter" },
    { doc_type: "successors_manual", title: "The Successor's Manual" },
  ];
  const businessOptedIn =
    ctx.branchFlags.has_operating_business &&
    ctx.decisions?.asset_rules?.business?.prepare_acceptance !== false;
  if (businessOptedIn) {
    list.push({ doc_type: "successor_manager_acceptance", title: "Successor Manager Acceptance" });
  }
  if (ctx.decisions?.minor_plan) {
    list.push({ doc_type: "readiness_transition_resolution", title: "Readiness Transition Resolution (prepared in advance)" });
  }
  return list;
}

function StatusChip({ status, exists }: { status?: string; exists: boolean }) {
  if (!exists) return <Badge variant="outline">Prepare</Badge>;
  if (status === "signed_filed") return <Badge className="bg-emerald-600 hover:bg-emerald-600">Signed and filed</Badge>;
  return <Badge variant="secondary">Drafted</Badge>;
}

export default function SuccessionDocuments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [unaId, setUnaId] = useState<string | null>(null);
  const [unaName, setUnaName] = useState("");
  const [profileId, setProfileId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [branchFlags, setBranchFlags] = useState<Record<string, boolean>>({});
  const [decisions, setDecisions] = useState<Record<string, any>>({});
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [hasOpenFlag, setHasOpenFlag] = useState(false);
  const [openDoc, setOpenDoc] = useState<string | null>(null);
  const [protectorOffice, setProtectorOffice] = useState<{ status: string; holder_name: string | null } | null>(null);

  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      const una = await resolveAdminUna(user.id);
      if (!una) { toast.error("Complete your Association setup first."); navigate("/dashboard"); return; }
      setUnaId(una.id); setUnaName(una.name);

      const { data: prof } = await supabase
        .from("succession_profiles")
        .select("*").eq("association_id", una.id)
        .order("created_at", { ascending: false }).limit(1).maybeSingle();
      if (!prof) { toast.error("Complete your Succession Profile first."); navigate("/succession"); return; }
      const d = (prof.decisions as any) || {};
      if (!d._complete) { toast.error("Complete the Decisions stage first."); navigate("/succession/decisions"); return; }
      setProfileId(prof.id);
      setAnswers((prof.answers as any) || {});
      setBranchFlags((prof.branch_flags as any) || {});
      setDecisions(d);

      const { data: flags } = await supabase
        .from("succession_review_flags").select("id").eq("profile_id", prof.id).is("resolved_at", null);
      setHasOpenFlag((flags?.length ?? 0) > 0);

      const { data: rows } = await supabase
        .from("succession_documents").select("*").eq("profile_id", prof.id);
      setDocs((rows as any) || []);

      if (d?.protector?.enabled === "yes") {
        const { data: offices } = await supabase
          .from("association_offices")
          .select("status, holder_name, office_name")
          .eq("association_id", una.id);
        const match = (offices || []).find(
          (o: any) => (o.office_name || "").toLowerCase().includes("steward protector"),
        );
        setProtectorOffice(match ? { status: (match as any).status, holder_name: (match as any).holder_name } : null);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const ctx: RenderContext = useMemo(
    () => ({ unaName, answers, branchFlags, decisions }),
    [unaName, answers, branchFlags, decisions],
  );
  const applicable = useMemo(() => buildApplicable(ctx), [ctx]);
  const docByType = useMemo(() => {
    const m = new Map<string, DocRow>();
    docs.forEach((d) => m.set(d.doc_type, d));
    return m;
  }, [docs]);

  async function refreshDocs() {
    if (!profileId) return;
    const { data } = await supabase
      .from("succession_documents").select("*").eq("profile_id", profileId);
    setDocs((data as any) || []);
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (hasOpenFlag) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto p-6">
          <Card className="p-8 text-center space-y-3">
            <ShieldAlert className="h-10 w-10 mx-auto text-amber-600" />
            <h1 className="text-2xl font-serif">Paused for review</h1>
            <p className="text-muted-foreground">
              This stage is on hold while our team looks over your profile. We will reach out shortly.
            </p>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const openDocConfig = openDoc ? applicable.find((a) => a.doc_type === openDoc) : null;

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
          </Button>
        </div>

        <div className="space-y-2">
          <h1 className="text-3xl font-serif">Your documents</h1>
          <p className="text-muted-foreground">
            Your decisions turn into paper here. Each one is a sample you sign as your own.
          </p>
        </div>

        {(() => {
          const allDrafted = applicable.every((a) => docByType.get(a.doc_type));
          const continuityFiled = docByType.get("continuity_resolution")?.status === "signed_filed";
          if (allDrafted && continuityFiled && !openDoc) {
            return (
              <Card className="p-4 border-emerald-500/40 bg-emerald-500/5">
                <p className="text-sm">
                  <strong>Stage complete.</strong> Execution comes next: signatures, banks, and the key map.
                </p>
              </Card>
            );
          }
          return null;
        })()}

        {openDocConfig ? (
          <TeachThenSignFlow
            key={openDoc}
            docType={openDocConfig.doc_type}
            title={openDocConfig.title}
            row={docByType.get(openDocConfig.doc_type) || null}
            ctx={ctx}
            profileId={profileId!}
            associationId={unaId!}
            userId={user!.id}
            onClose={() => setOpenDoc(null)}
            onSaved={refreshDocs}
          />
        ) : (
          <div className="grid gap-3">
            {applicable.map((a) => {
              const row = docByType.get(a.doc_type);
              return (
                <button
                  key={a.doc_type}
                  onClick={() => setOpenDoc(a.doc_type)}
                  className="text-left rounded-md border p-4 hover:border-primary hover:bg-accent transition"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div>
                        <div className="font-medium">{a.title}</div>
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {DOC_TEACHING[a.doc_type]?.body}
                        </div>
                      </div>
                    </div>
                    <StatusChip status={row?.status} exists={!!row} />
                  </div>
                </button>
              );
            })}

            {decisions?.protector?.enabled === "yes" && (
              <ProtectorCard office={protectorOffice} />
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
}

/* ---------- Protector card (not a document) ---------- */

function ProtectorCard({ office }: { office: { status: string; holder_name: string | null } | null }) {
  const navigate = useNavigate();
  const active = office && office.status === "active";
  return (
    <div className="rounded-md border p-4 bg-muted/30">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="font-medium flex items-center gap-2">
            Steward Protector
            {active ? <Badge className="bg-emerald-600 hover:bg-emerald-600">Adopted</Badge>
              : <Badge variant="outline">Not yet adopted</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {active
              ? `Held by ${office!.holder_name || "the named steward"}.`
              : "Your Steward Protector office is not yet in place."}
          </p>
        </div>
        {!active && (
          <Button variant="outline" size="sm" onClick={() => navigate("/governance/offices?charter=steward_protector")}>
            <ExternalLink className="h-4 w-4 mr-1" /> Open Office Builder
          </Button>
        )}
      </div>
    </div>
  );
}

/* ---------- Teach Then Sign flow ---------- */

function renderDoc(docType: string, ctx: RenderContext, content: Record<string, any>) {
  switch (docType) {
    case "continuity_resolution": return <ContinuityResolutionDoc ctx={ctx} />;
    case "standing_successor_resolution": return <StandingSuccessorResolutionDoc ctx={ctx} />;
    case "incapacity_protocol": return <IncapacityProtocolDoc ctx={ctx} />;
    case "founders_letter": return <FoundersLetterDoc ctx={ctx} content={content || {}} />;
    case "successors_manual": return <SuccessorsManualDoc ctx={ctx} />;
    case "successor_manager_acceptance": return <SuccessorManagerAcceptanceDoc ctx={ctx} />;
    case "readiness_transition_resolution": return <ReadinessTransitionResolutionDoc ctx={ctx} />;
    default: return null;
  }
}

function TeachThenSignFlow({
  docType, title, row, ctx, profileId, associationId, userId, onClose, onSaved,
}: {
  docType: string; title: string; row: DocRow | null; ctx: RenderContext;
  profileId: string; associationId: string; userId: string;
  onClose: () => void; onSaved: () => void;
}) {
  const teach = DOC_TEACHING[docType];
  const [phase, setPhase] = useState<"teach" | "render">(row ? "render" : "teach");
  const [saving, setSaving] = useState(false);
  const [filing, setFiling] = useState(false);
  const [confirmFile, setConfirmFile] = useState(false);
  const [content, setContent] = useState<Record<string, any>>(row?.content || {});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const isFounders = docType === "founders_letter";

  async function ensureDraft(payloadContent?: Record<string, any>): Promise<string | null> {
    if (row?.id) {
      const { error } = await supabase
        .from("succession_documents")
        .update({ content: payloadContent ?? content })
        .eq("id", row.id);
      if (error) { toast.error("Could not save."); return null; }
      return row.id;
    }
    const { data, error } = await supabase
      .from("succession_documents")
      .insert({
        association_id: associationId,
        profile_id: profileId,
        doc_type: docType,
        title,
        status: "drafted",
        content: payloadContent ?? content,
        created_by: userId,
      })
      .select("*").maybeSingle();
    if (error || !data) { toast.error("Could not save draft."); return null; }
    return (data as any).id;
  }

  async function handleSaveDraft() {
    setSaving(true);
    const id = await ensureDraft();
    setSaving(false);
    if (id) { toast.success("Draft saved."); onSaved(); }
  }

  // Autosave for Founder's Letter content
  useEffect(() => {
    if (!isFounders) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      await ensureDraft(content);
      onSaved();
    }, 800);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  function handlePrint() {
    const node = printRef.current;
    if (!node) return;
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) { toast.error("Please allow pop-ups to print."); return; }
    w.document.write(`
<!doctype html><html><head><title>${title}</title>
<link rel="stylesheet" href="${window.location.origin}/index.css" />
<style>body{margin:0;padding:24px;font-family:Georgia,serif;color:#000;background:#fff}</style>
</head><body>${node.outerHTML}</body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 300);
  }

  async function handleMarkFiled() {
    setFiling(true);
    const id = await ensureDraft();
    if (!id) { setFiling(false); return; }
    const { error } = await supabase
      .from("succession_documents")
      .update({ status: "signed_filed", filed_at: new Date().toISOString() })
      .eq("id", id);
    setFiling(false);
    setConfirmFile(false);
    if (error) { toast.error("Could not mark as filed."); return; }
    toast.success("Marked signed and filed.");
    onSaved();
    onClose();
  }

  if (phase === "teach") {
    return (
      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to your documents
          </Button>
        </div>
        <h2 className="text-2xl font-serif">{teach?.title || title}</h2>
        <p className="leading-relaxed">{teach?.body}</p>
        <div className="pt-4">
          <Button onClick={() => setPhase("render")}>Continue</Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onClose}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to your documents
        </Button>
        <div className="text-sm text-muted-foreground">{title}</div>
      </div>

      {isFounders && (
        <Card className="p-6 space-y-4">
          <h3 className="font-serif text-lg">Write in your own voice</h3>
          <p className="text-sm text-muted-foreground">Six prompts. Save as you go. Nothing here is legal wording.</p>
          {FOUNDERS_PROMPTS.map((p) => (
            <div key={p.key} className="space-y-1">
              <label className="text-sm font-medium">{p.label}</label>
              <p className="text-xs text-muted-foreground">{p.helper}</p>
              <Textarea
                value={content?.[p.key] || ""}
                onChange={(e) => setContent((c) => ({ ...c, [p.key]: e.target.value }))}
                rows={4}
              />
            </div>
          ))}
        </Card>
      )}

      <div ref={printRef}>
        {renderDoc(docType, ctx, content)}
      </div>

      <Card className="p-4">
        <h4 className="font-medium mb-1">Execution guidance</h4>
        <p className="text-sm text-muted-foreground">{DOC_GUIDANCE[docType]}</p>
      </Card>

      <div className="flex flex-wrap gap-2 justify-end">
        <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
          Save draft
        </Button>
        <Button variant="outline" onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-1" /> Print view
        </Button>
        <Button onClick={() => setConfirmFile(true)} disabled={filing}>
          <Check className="h-4 w-4 mr-1" /> Mark signed and filed
        </Button>
      </div>

      <AlertDialog open={confirmFile} onOpenChange={setConfirmFile}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm the paper is filed</AlertDialogTitle>
            <AlertDialogDescription>
              Confirm the paper is signed in blue ink, stamped by a notary where the paper says so (a notary stamps to prove you signed), scanned, and filed in the Record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkFiled} disabled={filing}>
              {filing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Yes, it is filed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
